import React from 'react';
import { MessageSquare, FileText, ClipboardCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AiActivityEvent, AiEventType, OpsScanStatus } from '@/types/dashboard';

interface AiActivityFeedItemProps {
  event: AiActivityEvent;
  className?: string;
}

const TYPE_ICON_MAP: Record<AiEventType, React.ElementType> = {
  review_queue: ClipboardCheck,
  chat_session: MessageSquare,
  transcript_analysis: FileText,
};

const TYPE_COLOR_MAP: Record<AiEventType, string> = {
  review_queue: 'text-running-ink bg-running-soft',
  chat_session: 'text-brand-dark bg-brand-tint',
  transcript_analysis: 'text-pass bg-pass-soft',
};

const SCAN_BADGE_MAP: Record<OpsScanStatus, { dot: string; label: string; bg: string }> = {
  passed: { dot: 'bg-pass', label: 'Passed', bg: 'bg-pass-soft text-pass' },
  failed: { dot: 'bg-fail', label: 'Failed', bg: 'bg-fail-soft text-fail' },
  pending: { dot: 'bg-warn-fill', label: 'Pending', bg: 'bg-warn-soft text-warn' },
};

function formatRelativeTime(timestamp: string): string {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

export function AiActivityFeedItem({ event, className }: AiActivityFeedItemProps) {
  const Icon = TYPE_ICON_MAP[event.type];
  const iconColorClass = TYPE_COLOR_MAP[event.type];
  const badge = SCAN_BADGE_MAP[event.opsScanStatus];

  return (
    <div className={cn('flex items-start gap-3 py-3', className)}>
      {/* Icon circle */}
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
          iconColorClass,
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-ink leading-snug">{event.description}</p>
        {event.memberName !== null && (
          <p className="text-xs text-ink-muted mt-0.5">{event.memberName}</p>
        )}
      </div>

      {/* Right: timestamp + badge */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className="text-xs text-ink-muted">{formatRelativeTime(event.timestamp)}</span>
        <span
          className={cn(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium',
            badge.bg,
          )}
        >
          <span className={cn('inline-block w-1.5 h-1.5 rounded-full', badge.dot)} />
          {badge.label}
        </span>
      </div>
    </div>
  );
}
